import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useEffect } from "react";
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  ClipboardCheck,
  BarChart3,
  ShieldCheck,
  Plug,
  SlidersHorizontal,
  CreditCard,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}
interface NavGroup {
  label: string;
  subtitle: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Content",
    subtitle: "Build brands and plan campaigns",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/brands", label: "Brands & Campaigns", icon: Building2 },
      { to: "/calendar", label: "Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Review",
    subtitle: "Approve and publish posts",
    items: [{ to: "/review", label: "Review Queue", icon: ClipboardCheck }],
  },
  {
    label: "Insights",
    subtitle: "Track performance and engagement",
    items: [{ to: "/analytics", label: "Analytics", icon: BarChart3 }],
  },
  {
    label: "Account",
    subtitle: "Security, platforms, and billing",
    items: [
      { to: "/settings/security", label: "Security", icon: ShieldCheck },
      {
        to: "/settings/platforms",
        label: "Connected Platforms",
        icon: Plug,
      },
      {
        to: "/settings/preferences",
        label: "Preferences",
        icon: SlidersHorizontal,
      },
      { to: "/settings/team", label: "Team", icon: Users },
      { to: "/billing", label: "Billing", icon: CreditCard },
    ],
  },
];

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useClerk();
  const { user } = useUser();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    onClose?.();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const email =
    user?.primaryEmailAddress?.emailAddress ??
    user?.emailAddresses?.[0]?.emailAddress ??
    "";

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-gray-200">
        <img
          src="/images/media-dingo-logo.svg"
          alt="Media Dingo"
          className="h-[72px] w-auto object-contain"
        />
        {/* Close button — mobile only */}
        <button
          onClick={onClose}
          className="lg:hidden p-1 -mr-1 text-gray-400 hover:text-gray-600"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {NAV_GROUPS.map((group, groupIndex) => (
          <div key={group.label} className={groupIndex > 0 ? "mt-5" : ""}>
            <div className="px-3 mb-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-600">
                {group.label}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                {group.subtitle}
              </p>
            </div>

            <div className="space-y-0.5 mt-1.5">
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-brand-50 text-brand-700"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div className="px-4 py-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 truncate mb-2">{email}</p>
        <button
          onClick={handleLogout}
          className="text-xs text-gray-500 hover:text-red-600 transition-colors"
        >
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — always visible */}
      <aside className="hidden lg:flex w-56 flex-shrink-0 bg-white border-r border-gray-200 flex-col">
        {sidebarContent}
      </aside>

      {/* Mobile overlay + drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40"
            onClick={onClose}
            aria-hidden
          />
          {/* Drawer */}
          <aside className="relative w-64 max-w-[80vw] bg-white flex flex-col shadow-xl">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
